
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { SermonPlan } from "./types";

export function useSermonPlansFetch() {
  const { toast } = useToast();
  const [sermonPlans, setSermonPlans] = useState<SermonPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  // Add network status listener
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const fetchSermonPlans = async () => {
    if (isOffline) {
      toast({
        title: "You're offline",
        description: "Cannot fetch sermon plans while offline. Please check your connection.",
        variant: "destructive",
      });
      setLoading(false);
      return [];
    }
    
    // Don't attempt to fetch too many times in a row
    if (retryCount > 3) {
      setLoading(false);
      setError(new Error("Too many failed attempts. Please try again later."));
      return [];
    }

    setLoading(true);
    setError(null);
    
    try {
      const { data: sermonPlansData, error: sermonPlansError } = await supabase
        .from("sermon_plans")
        .select(`
          *,
          congregations (
            name
          )
        `)
        .order("planned_date", { ascending: false });

      if (sermonPlansError) throw sermonPlansError;
      
      // Transform data to include auto_generated field if it doesn't exist
      const transformedData: SermonPlan[] = (sermonPlansData || []).map((plan: any) => ({
        ...plan,
        auto_generated: plan.auto_generated ?? false
      }));
      
      setSermonPlans(transformedData);
      setRetryCount(0); // Reset retry count on success
      return transformedData;
    } catch (error: any) {
      console.error("Error fetching sermon plans:", error);
      
      // Increment retry count
      setRetryCount(prev => prev + 1);
      
      // Set error state
      setError(error instanceof Error ? error : new Error(String(error)));
      
      // Only show toast for the first error to avoid spamming
      if (retryCount === 0) {
        toast({
          title: "Connection Error",
          description: "Failed to load sermon plans. Please check your internet connection.",
          variant: "destructive",
        });
      }
      
      return [];
    } finally {
      // Ensure loading state is reset even if there's an error
      setLoading(false);
    }
  };

  return {
    sermonPlans,
    setSermonPlans,
    fetchSermonPlans,
    loading,
    setLoading,
    error,
    isOffline
  };
}
